import { useCallback, useMemo, useState } from 'react';

import { useGetStaticAssetUrl } from '@change-corgi/core-react/assets';
import { useI18n } from '@change-corgi/core-react/i18n';

import { useLocale } from '#src/app/shared/hooks/l10n';

const BASE_PATH = '/flyer-templates/flyer-a/flyer-a';

export type FlyerPreviewImage = {
	/**
	 * `srcset` for a WebP `<source>`. Undefined for the non-locale fallback, which is PNG only.
	 */
	webpSrcSet: string | undefined;
	src: string;
	srcSet: string;
	alt: string;
};

type Source = 'locale' | 'fallback' | 'failed';

type UseFlyerPreviewImageReturn = {
	/**
	 * Undefined once both the locale image and the non-locale fallback failed to load.
	 */
	previewImage: FlyerPreviewImage | undefined;
	/**
	 * To call from the preview `<img>`'s `onError`: moves to the non-locale fallback, then to no image.
	 */
	handlePreviewImageError: () => void;
};

/**
 * The static Flyer A preview image for the current locale, falling back to the non-locale image when
 * the locale one is missing.
 */
export function useFlyerPreviewImage(): UseFlyerPreviewImageReturn {
	const { translate } = useI18n();
	const locale = useLocale();
	const getStaticAssetUrl = useGetStaticAssetUrl();
	const [source, setSource] = useState<Source>('locale');

	// FIXME: key name still references the starter dashboard; kept as-is so translated locales keep the string
	const alt = translate('corgi.petition.starter_dashboard.shared.flyer.flyer_a_alt_text');

	const previewImage = useMemo((): FlyerPreviewImage | undefined => {
		if (source === 'failed') return undefined;

		const basePath = source === 'locale' ? `${BASE_PATH}-${locale}` : BASE_PATH;
		const src = getStaticAssetUrl(`${basePath}.png`);
		return {
			webpSrcSet:
				source === 'locale'
					? `${getStaticAssetUrl(`${basePath}.webp`)} 1x, ${getStaticAssetUrl(`${basePath}-2x.webp`)} 2x`
					: undefined,
			src,
			srcSet: `${src} 1x, ${getStaticAssetUrl(`${basePath}-2x.png`)} 2x`,
			alt,
		};
	}, [alt, getStaticAssetUrl, locale, source]);

	const handlePreviewImageError = useCallback(() => {
		setSource((current) => (current === 'locale' ? 'fallback' : 'failed'));
	}, []);

	return { previewImage, handlePreviewImageError };
}
